import styled from 'styled-components';
import { IonButton } from '@ionic/react';
import { ComponentProps, ForwardRefExoticComponent, RefAttributes } from 'react';
import { AllieButtonCorner } from '../../Buttons/Button/Button.types';
import { AllieInputSize, AllieInputType } from '../Input/Input.types';
import { StyledDateTimeButtonProps } from './DateTimeInput.types';

const defaults = {
  allieType: 'primary' as AllieInputType,
  allieSize: 'normal' as AllieInputSize,
  corner: 'rounded' as AllieButtonCorner,
  disabled: false,
  shadow: false,
};

const getBackground = (allieType = defaults.allieType, disabled = defaults.disabled) => {
  if (disabled) return 'transparent';
  if (allieType === 'secondary') return 'transparent';
  return 'light-main';
};

const getBorderColor = (allieType = defaults.allieType) => {
  if (allieType === 'primary') return 'light-40';
  if (allieType === 'secondary') return 'primary-60';
  if (allieType === 'warning') return 'warning-50';
  if (allieType === 'error') return 'error-50';
  return 'light-40';
};

const getPadding = (allieSize = defaults.allieSize) => {
  if (allieSize === 'small') return 'var(--allie-spacing-md, 8px) var(--allie-spacing-lg, 12px)';
  if (allieSize === 'medium') return '10px var(--allie-spacing-xl, 14px)';
  return 'var(--allie-spacing-lg, 12px) var(--allie-spacing-xl, 16px)';
};

const StyledDateTimeButtonBase = styled(IonButton)
  .withConfig({
    shouldForwardProp: (prop) => !['allieType', 'allieSize', 'corner', 'shadow'].includes(prop),
  })
  .attrs<StyledDateTimeButtonProps>(() => ({}))<StyledDateTimeButtonProps>`
  --padding-start: 0;
  --ripple-color: transparent;

  display: flex;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  padding: ${(props) => getPadding(props.allieSize)};
  background: var(--allie-color-${(props) => getBackground(props.allieType, props.disabled)});
  border: 1px solid var(--allie-color-${(props) => getBorderColor(props.allieType)});
  border-radius: ${(props) =>
    props.corner === 'rounded' ? 'var(--allie-radius-md, 8px)' : 'var(--allie-radius-md, 8px)'};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  margin: 0;
  width: ${(props) => (props.expand === 'full' ? '100%' : 'fit-content')};
  box-sizing: border-box;
  min-height: 39px;
`;

export const StyledDateTimeContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  font-family: 'Hanken Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: var(--allie-color-neutral-90);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;

  svg,
  ion-icon {
    width: 100%;
    height: 100%;
  }
`;

// Exportar con type assertion para evitar referencia a @ionic/core en el tipo inferido
export const StyledDateTimeButton = StyledDateTimeButtonBase as ForwardRefExoticComponent<
  ComponentProps<typeof IonButton> & RefAttributes<HTMLIonButtonElement> & StyledDateTimeButtonProps
>;
